/**
 * NumericInput — input numérico que muestra el valor 0 como placeholder
 * (marca de agua) en vez de "0" pegado dentro del campo.
 *
 * Comportamiento:
 *  - Si value === 0 (o "0" o null/undefined), el input se ve VACÍO con un
 *    placeholder "0" (gris claro). No es necesario borrarlo antes de escribir.
 *  - Cuando el usuario escribe, se envía un número via onChange. Si borra
 *    todo, devuelve 0 (no NaN). El padre puede seguir guardando 0 como antes.
 *
 * Drop-in replacement para `<Input type="number" />` en formularios de líneas
 * (cantidad, precio, descuento) donde un 0 inicial estorba.
 */
import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";

export const NumericInput = React.forwardRef(function NumericInput(
  { value, onChange, placeholder, decimals = 4, allowZero = false, ...props },
  ref
) {
  // `text` es el string que ve el usuario; permite vaciar el campo sin que
  // React lo rellene con "0" automáticamente.
  const numericValue = (typeof value === "string" ? parseFloat(value) : value) || 0;
  const initial = numericValue === 0 ? "" : String(value);
  const [text, setText] = useState(initial);

  // Sincronizar cuando el padre cambia el valor desde fuera (ej. recálculo)
  useEffect(() => {
    const externalNum = (typeof value === "string" ? parseFloat(value) : value) || 0;
    const localNum = parseFloat(text);
    // Si el valor externo no concuerda con lo que el usuario está escribiendo,
    // sobrescribimos (solo si difieren más allá de epsilon)
    if (Math.abs(externalNum - (isNaN(localNum) ? 0 : localNum)) > 1e-9) {
      setText(externalNum === 0 ? "" : String(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const ph = placeholder ?? (allowZero ? "" : "0");

  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);
    if (v === "" || v === "-" || v === ".") {
      onChange?.({ ...e, target: { ...e.target, value: 0 } });
      return;
    }
    const n = parseFloat(v);
    if (!isNaN(n)) {
      onChange?.({ ...e, target: { ...e.target, value: n } });
    }
  };

  const handleFocus = (e) => {
    // Selecciona todo el contenido para que escribir lo reemplace
    try { e.target.select(); } catch (_) { /* ignore */ }
    props.onFocus?.(e);
  };

  return (
    <Input
      ref={ref}
      type="number"
      step={`0.${"0".repeat(Math.max(0, decimals - 1))}1`}
      value={text}
      placeholder={ph}
      onChange={handleChange}
      onFocus={handleFocus}
      {...props}
      onFocusCapture={undefined}
    />
  );
});

export default NumericInput;
